import React, { useState, useEffect, useRef, useMemo } from "react";
import ReactDOM from "react-dom";

// ── Status badge config ──
const CT_STATUS_BADGE = {
    // ── uppercase_underscore (what backend actually returns) ──
    "COMPLETED":                  "discovery-badge-green",
    "RECRUITING":                 "discovery-badge-blue",
    "NOT_YET_RECRUITING":         "discovery-badge-yellow",
    "ACTIVE_NOT_RECRUITING":      "discovery-badge-orange",
    "TERMINATED":                 "discovery-badge-red",
    "WITHDRAWN":                  "discovery-badge-red",
    "SUSPENDED":                  "discovery-badge-orange",
    "ENROLLING_BY_INVITATION":    "discovery-badge-blue",
    "UNKNOWN":                    "discovery-badge-neutral",
    // ── title case fallbacks (in case some records use different format) ──
    "Completed":                  "discovery-badge-green",
    "Recruiting":                 "discovery-badge-blue",
    "Not yet recruiting":         "discovery-badge-yellow",
    "Active, not recruiting":     "discovery-badge-orange",
    "Terminated":                 "discovery-badge-red",
    "Withdrawn":                  "discovery-badge-red",
    "Suspended":                  "discovery-badge-orange",
    "Enrolling by invitation":    "discovery-badge-blue",
    "Unknown status":             "discovery-badge-neutral",
};

const CT_PHASE_BADGE = {
    "Phase 1":        "ev-badge-pill ev-fs-11 ev-badge-light",
    "Phase 2":        "ev-badge-pill ev-fs-11 ev-badge-light",
    "Phase 3":        "ev-badge-pill ev-fs-11 ev-badge-blue",
    "Phase 4":        "ev-badge-pill ev-fs-11 ev-badge-purple",
    "Early Phase 1":  "ev-badge-pill ev-fs-11 ev-badge-light",
    "Phase 1/2":      "ev-badge-pill ev-fs-11 ev-badge-light",
    "Phase 2/3":      "ev-badge-pill ev-fs-11 ev-badge-light",
    "N/A":            "ev-badge-pill ev-fs-11 ev-badge-light",
};

// ── Default filter state ──
const DEFAULT_FILTERS = {
    study_status:   [],
    study_phase:    [],
    study_type:     [],
    sex:            "all",
    age:            [],
    has_results:    null,
    funder_type:    [],
    date_start_from: "",
    date_start_to:   "",
    date_completion_from: "",
    date_completion_to:   "",
    sponsor:        "",
    title:          "",
    outcome:        "",
};

// ── Count active filters ──
const countActiveFilters = (f) => {
    let count = 0;
    if (f.study_status.length)   count++;
    if (f.study_phase.length)    count++;
    if (f.study_type.length)     count++;
    if (f.sex !== "all")         count++;
    if (f.age.length)            count++;
    if (f.has_results !== null)  count++;
    if (f.funder_type.length)    count++;
    if (f.date_start_from || f.date_start_to) count++;
    if (f.date_completion_from || f.date_completion_to) count++;
    if (f.sponsor.trim())        count++;
    if (f.title.trim())          count++;
    if (f.outcome.trim())        count++;
    return count;
};

// ── Toggle helper ──
const toggleArr = (arr, val) =>
    arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val];

// ── Filter Panel ──
const CTFilterPanel = ({ filters, onChange, onApply, onClear, onClose }) => {
    const [local, setLocal] = useState({ ...filters });

    const set = (key, val) => setLocal((prev) => ({ ...prev, [key]: val }));
    const toggleList = (key, val) => setLocal((prev) => ({ ...prev, [key]: toggleArr(prev[key], val) }));

    const STUDY_STATUS_GROUPS = [
        { group: "Recruiting",
            items: ["NOT_YET_RECRUITING", "RECRUITING"],
        },
        { group: "Active",
            items: ["ACTIVE_NOT_RECRUITING", "COMPLETED", "TERMINATED"],
        },
        { group: "Other",
            items: ["ENROLLING_BY_INVITATION", "SUSPENDED", "WITHDRAWN", "UNKNOWN"],
        },
    ];

    const PHASES = ["Early Phase 1", "Phase 1", "Phase 2", "Phase 3", "Phase 4", "Not applicable"];
    const STUDY_TYPES = ["Interventional", "Observational", "Patient registries", "Expanded access"];
    const FUNDER_TYPES = ["NIH", "Other U.S. federal agency", "Industry", "All others (individuals, universities, organizations)"];
    const AGE_GROUPS = [
        { key: "child",       label: "Child (birth – 17)" },
        { key: "adult",       label: "Adult (18 – 64)" },
        { key: "older_adult", label: "Older adult (65+)" },
    ];

    return (
        <div
            className="ev-message-card2 ev-card-3 ev-radius-xsmall shadow"
            style={{ width: "380px" }}
            onClick={(e) => e.stopPropagation()}
        >
            {/* Scrollable content */}
            <div className="scrollable-element px-3 pt-3 pb-1" style={{ maxHeight: "340px" }}>

                {/* Study Status */}
                <p className="ev-xsmall-p-dark ev-bold-med mb-2">Study Status</p>
                {STUDY_STATUS_GROUPS.map((grp) => (
                    <div key={grp.group} className="mb-2">
                        <p className="ev-xsmall-p-dark text-muted mb-1">{grp.group}</p>
                        {grp.items.map((item) => (
                            <div key={item} className="form-check mb-1 ms-2">
                                <input
                                    type="checkbox"
                                    className="form-check-input"
                                    id={`status-${item}`}
                                    checked={local.study_status.includes(item)}
                                    onChange={() => toggleList("study_status", item)}
                                />
                                <label className="form-check-label ev-xsmall-p-dark" htmlFor={`status-${item}`}>
                                    {item.replace(/_/g, ' ').toLowerCase().replace(/^\w/, c => c.toUpperCase())}                                </label>
                            </div>
                        ))}
                    </div>
                ))}

                <hr className="my-2" />

                {/* Study Phase */}
                <p className="ev-xsmall-p-dark ev-bold-med mb-2">Study Phase</p>
                {PHASES.map((p) => (
                    <div key={p} className="form-check mb-1">
                        <input
                            type="checkbox"
                            className="form-check-input"
                            id={`phase-${p}`}
                            checked={local.study_phase.includes(p)}
                            onChange={() => toggleList("study_phase", p)}
                        />
                        <label className="form-check-label ev-xsmall-p-dark" htmlFor={`phase-${p}`}>{p}</label>
                    </div>
                ))}

                <hr className="my-2" />

                {/* Study Type */}
                <p className="ev-xsmall-p-dark ev-bold-med mb-2">Study Type</p>
                {STUDY_TYPES.map((t) => (
                    <div key={t} className="form-check mb-1">
                        <input
                            type="checkbox"
                            className="form-check-input"
                            id={`type-${t}`}
                            checked={local.study_type.includes(t)}
                            onChange={() => toggleList("study_type", t)}
                        />
                        <label className="form-check-label ev-xsmall-p-dark" htmlFor={`type-${t}`}>{t}</label>
                    </div>
                ))}

                <hr className="my-2" />

                {/* Eligibility Criteria */}
                <p className="ev-xsmall-p-dark ev-bold-med mb-2">Eligibility Criteria</p>
                <p className="ev-xsmall-p-dark text-muted mb-1">Sex</p>
                {["all", "female", "male"].map((s) => (
                    <div key={s} className="form-check mb-1">
                        <input
                            type="radio"
                            className="form-check-input"
                            id={`sex-${s}`}
                            name="sex"
                            checked={local.sex === s}
                            onChange={() => set("sex", s)}
                        />
                        <label className="form-check-label ev-xsmall-p-dark" htmlFor={`sex-${s}`}>
                            {s === "all" ? "All" : s.charAt(0).toUpperCase() + s.slice(1)}
                        </label>
                    </div>
                ))}
                <p className="ev-xsmall-p-dark text-muted mt-2 mb-1">Age</p>
                {AGE_GROUPS.map((a) => (
                    <div key={a.key} className="form-check mb-1">
                        <input
                            type="checkbox"
                            className="form-check-input"
                            id={`age-${a.key}`}
                            checked={local.age.includes(a.key)}
                            onChange={() => toggleList("age", a.key)}
                        />
                        <label className="form-check-label ev-xsmall-p-dark" htmlFor={`age-${a.key}`}>{a.label}</label>
                    </div>
                ))}

                <hr className="my-2" />

                {/* Study Results */}
                <p className="ev-xsmall-p-dark ev-bold-med mb-2">Study Results</p>
                {[{ val: null, label: "All" }, { val: true, label: "With results" }, { val: false, label: "Without results" }].map((opt) => (
                    <div key={String(opt.val)} className="form-check mb-1">
                        <input
                            type="radio"
                            className="form-check-input"
                            id={`results-${opt.val}`}
                            name="has_results"
                            checked={local.has_results === opt.val}
                            onChange={() => set("has_results", opt.val)}
                        />
                        <label className="form-check-label ev-xsmall-p-dark" htmlFor={`results-${opt.val}`}>{opt.label}</label>
                    </div>
                ))}

                <hr className="my-2" />

                {/* Funder Type */}
                <p className="ev-xsmall-p-dark ev-bold-med mb-2">Funder Type</p>
                {FUNDER_TYPES.map((f) => (
                    <div key={f} className="form-check mb-1">
                        <input
                            type="checkbox"
                            className="form-check-input"
                            id={`funder-${f}`}
                            checked={local.funder_type.includes(f)}
                            onChange={() => toggleList("funder_type", f)}
                        />
                        <label className="form-check-label ev-xsmall-p-dark" htmlFor={`funder-${f}`}>{f}</label>
                    </div>
                ))}

                <hr className="my-2" />

                {/* Date Range */}
                <p className="ev-xsmall-p-dark ev-bold-med mb-2">Date Range</p>
                <p className="ev-xsmall-p-dark text-muted mb-1">Study Start</p>
                <div className="d-flex gap-2 mb-2">
                    <div className="flex-fill">
                        <label className="ev-xsmall-p-dark mb-1">From</label>
                        <input type="date" className="formField2 ev-btn-small w-100 ev-radius-xsmall"
                            value={local.date_start_from}
                            onChange={(e) => set("date_start_from", e.target.value)} />
                    </div>
                    <div className="flex-fill">
                        <label className="ev-xsmall-p-dark mb-1">To</label>
                        <input type="date" className="formField2 ev-btn-small w-100 ev-radius-xsmall"
                            value={local.date_start_to}
                            onChange={(e) => set("date_start_to", e.target.value)} />
                    </div>
                </div>
                <p className="ev-xsmall-p-dark text-muted mb-1">Primary Completion</p>
                <div className="d-flex gap-2 mb-2">
                    <div className="flex-fill">
                        <label className="ev-xsmall-p-dark mb-1">From</label>
                        <input type="date" className="formField2 ev-btn-small w-100 ev-radius-xsmall"
                            value={local.date_completion_from}
                            onChange={(e) => set("date_completion_from", e.target.value)} />
                    </div>
                    <div className="flex-fill">
                        <label className="ev-xsmall-p-dark mb-1">To</label>
                        <input type="date" className="formField2 ev-btn-small w-100 ev-radius-xsmall"
                            value={local.date_completion_to}
                            onChange={(e) => set("date_completion_to", e.target.value)} />
                    </div>
                </div>

                <hr className="my-2" />

                {/* More Ways to Search */}
                <p className="ev-xsmall-p-dark ev-bold-med mb-2">More Ways to Search</p>
                <div className="mb-2">
                    <label className="ev-xsmall-p-dark mb-1">Title</label>
                    <input type="text" className="formField2 ev-btn-small w-100 ev-radius-xsmall"
                        placeholder="Title or title acronym"
                        value={local.title}
                        onChange={(e) => set("title", e.target.value)} />
                </div>
                <div className="mb-2">
                    <label className="ev-xsmall-p-dark mb-1">Outcome Measure</label>
                    <input type="text" className="formField2 ev-btn-small w-100 ev-radius-xsmall"
                        placeholder="Outcome measure"
                        value={local.outcome}
                        onChange={(e) => set("outcome", e.target.value)} />
                </div>
                <div className="mb-2">
                    <label className="ev-xsmall-p-dark mb-1">Sponsor / Collaborator</label>
                    <input type="text" className="formField2 ev-btn-small w-100 ev-radius-xsmall"
                        placeholder="Sponsor or collaborator name"
                        value={local.sponsor}
                        onChange={(e) => set("sponsor", e.target.value)} />
                </div>

            </div>

            {/* Sticky footer — Clear + Apply */}
            <div className="d-flex justify-content-between align-items-center px-3 py-2 border-top">
                <button
                    type="button"
                    className="ev-btn-blue-outline-round ev-btn-small ev-bold-reg"
                    onClick={() => {
                        setLocal({ ...DEFAULT_FILTERS });
                        onClear();
                    }}
                >
                    Clear Filters
                </button>
                <button
                    type="button"
                    className="ev-btn-blue-fill-round ev-btn-small ev-bold-reg"
                    onClick={() => {
                        onChange(local);
                        onApply(local);
                        onClose();
                    }}
                >
                    Apply Filters
                </button>
            </div>
        </div>
    );
};

// ── Apply filters to results array (frontend dummy filtering) ──
const applyFilters = (results, f) => {
    return results.filter((r) => {
        const status  = r.overall_status || "";
        const phase   = r.phase || "";
        const type    = r.study_type || "";
        const sponsor = r.lead_sponsor || "";
        const title   = r.brief_title || "";
        const startDate      = r.start_date || "";
        const completionDate = r.completion_date || "";

        if (f.study_status.length && !f.study_status.includes(status)) return false;
        if (f.study_phase.length  && !f.study_phase.includes(phase))   return false;
        if (f.study_type.length   && !f.study_type.includes(type))     return false;
        if (f.title.trim()  && !title.toLowerCase().includes(f.title.trim().toLowerCase()))   return false;
        if (f.sponsor.trim() && !sponsor.toLowerCase().includes(f.sponsor.trim().toLowerCase())) return false;
        if (f.outcome.trim()) {
            const po = r.primary_outcome || "";
            const so = r.secondary_outcome || "";
            const q  = f.outcome.trim().toLowerCase();
            if (!po.toLowerCase().includes(q) && !so.toLowerCase().includes(q)) return false;
        }
        if (f.date_start_from && startDate && startDate < f.date_start_from) return false;
        if (f.date_start_to   && startDate && startDate > f.date_start_to)   return false;
        if (f.date_completion_from && completionDate && completionDate < f.date_completion_from) return false;
        if (f.date_completion_to   && completionDate && completionDate > f.date_completion_to)   return false;

        return true;
    });
};

// ── Single trial row ──
const TrialRow = ({ trial, isActive, isChecked, onSelect, onCheck }) => {
    const status     = trial.overall_status || null;
    const phase      = trial.phase || null;
    const nctId      = trial.nct_id || "—";
    const conditions = Array.isArray(trial.conditions)
        ? trial.conditions.map(c => c.condition_name || c).filter(Boolean).join(", ")
        : "—";
    const locations  = Array.isArray(trial.locations) && trial.locations.length > 0
        ? trial.locations.slice(0, 2).map(l =>
            [l.city, l.country].filter(Boolean).join(", ") || l.facility || ""
          ).filter(Boolean).join("; ") +
          (trial.locations.length > 2 ? ` +${trial.locations.length - 2}` : "")
        : "—";

    return (
        <tr
            className={`cursor-pointer ${isActive ? "discovery-row-active" : ""}`}
            onClick={() => onSelect?.(trial)}
        >
            <td className="align-top" style={{ wordBreak: "break-word", whiteSpace: "normal" }}>
                <div className="d-flex align-items-start gap-2">
                    <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={(e) => {
                            e.stopPropagation();
                            onCheck?.(trial.id, e.target.checked);
                        }}
                    />
                    <div style={{ minWidth: 0 }}>
                        <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                            <span className="ev-badge-pill ev-fs-11 ev-badge-blue">{nctId}</span>
                            {phase && (
                                <span className={CT_PHASE_BADGE[phase] || "ev-badge-pill ev-fs-11 ev-badge-light"}>
                                    {phase}
                                </span>
                            )}
                        </div>
                        <p className="ev-reg-p-dark ev-bold-med mb-1" style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                            {trial.brief_title || "—"}
                        </p>
                        <p className="ev-small-p-dark mb-0" style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                            {conditions}
                        </p>
                        {locations !== "—" && (
                            <p className="ev-xsmall-p-dark mb-0">
                                <i className="bi bi-geo-alt me-1"></i>{locations}
                            </p>
                        )}
                    </div>
                </div>
            </td>

            <td className="align-top text-end" style={{ width: "30%" }}>
                <div className="d-flex flex-column align-items-end gap-1">
                    {status && (
                        <span className={CT_STATUS_BADGE[status] || "discovery-badge-neutral"}>
                            {status.replace(/_/g, " ").toLowerCase().replace(/^\w/, c => c.toUpperCase())}
                        </span>
                    )}
                    {trial.start_date && (
                        <span className="ev-xsmall-p-dark">{trial.start_date}</span>
                    )}
                </div>
            </td>
        </tr>
    );
};

// ── Main List ──
const ClinicalTrialsResultsList = ({
    results = [],
    selectedResult,
    onSelectResult,
    selectedIds = [],
    selectAll = false,
    onSelectAll,
    onSelectOne,
}) => {
    const [showSelectedOnly, setShowSelectedOnly] = useState(false);
    const [isFilterOpen, setIsFilterOpen]         = useState(false);
    const [appliedFilters, setAppliedFilters]      = useState({ ...DEFAULT_FILTERS });
    const [pendingFilters, setPendingFilters]      = useState({ ...DEFAULT_FILTERS });
    const [panelPos, setPanelPos]                  = useState({ top: 0, left: 0 });

    const buttonRef = useRef(null);
    const panelRef  = useRef(null);
    const selectedCount      = selectedIds.length;
    const activeFilterCount  = countActiveFilters(appliedFilters);

    // ── Open panel: calculate position from button ──
    const handleOpenFilter = () => {
        if (buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            setPanelPos({
                top:  rect.bottom + window.scrollY + 4,
                left: rect.left   + window.scrollX,
            });
        }
        setIsFilterOpen((p) => !p);
    };

    // ── Close on outside click ──
    useEffect(() => {
        const handleOutside = (e) => {
            if (
                panelRef.current  && !panelRef.current.contains(e.target) &&
                buttonRef.current && !buttonRef.current.contains(e.target)
            ) {
                setIsFilterOpen(false);
            }
        };
        if (isFilterOpen) document.addEventListener("mousedown", handleOutside);
        return () => document.removeEventListener("mousedown", handleOutside);
    }, [isFilterOpen]);

    useEffect(() => {
        if (showSelectedOnly && selectedCount === 0) setShowSelectedOnly(false);
    }, [showSelectedOnly, selectedCount]);

    // ── Apply filters ──
    const filteredResults = useMemo(
        () => applyFilters(results, appliedFilters),
        [results, appliedFilters]
    );

    const visibleResults = showSelectedOnly
        ? filteredResults.filter((r) => selectedIds.includes(r.id))
        : filteredResults;

    if (results.length === 0) {
        return (
            <div className="ev-card-2 p-4 vh-55 d-flex flex-column align-items-center justify-content-center text-center">
                <i className="bi bi-journal-x ev-fs-50 text-muted mb-3"></i>
                <p className="ev-reg-p-dark ev-bold-med mb-1">No Clinical Trials Found</p>
                <p className="ev-small-p-blue mb-0">Run a search to fetch results.</p>
            </div>
        );
    }

    return (
        <div className="table-container" style={{ overflowX: "hidden" }}>

            {/* ── Header row ── */}
            <table className="table-custom1 mt-0 overflow-visible w-100 ev-message-card3 ev-radius-xsmall py-0 px-2">
                <thead>
                    <tr>
                        <th className="p-0">
                            <div className="d-flex align-items-center gap-2 flex-wrap">
                                <input
                                    type="checkbox"
                                    checked={selectAll}
                                    onChange={(e) => onSelectAll?.(e.target.checked)}
                                />
                                <label className="mb-0">Trials ({filteredResults.length})</label>

                                <span className="ev-btn-white-fill-round ev-btn-xsmall ev-small-p-blue px-3 pe-1 ev-bold-med">
                                    {selectedCount} of {filteredResults.length} Selected
                                    <button
                                        type="button"
                                        className={`ev-btn-xsmall ev-bold-med ev-radius-xlarge ${
                                            showSelectedOnly
                                                ? "ev-btn-blue-fill-round ms-2"
                                                : "ev-btn-skyblue-fill-round ev-small-p-blue ms-2"
                                        }`}
                                        onClick={() => setShowSelectedOnly((prev) => !prev)}
                                        disabled={!showSelectedOnly && selectedCount === 0}
                                    >
                                        {showSelectedOnly ? "Show All" : "Show Selected"}
                                    </button>
                                </span>

                                {/* ── Filter trigger — Full Article Access style ── */}
                                <div className="ms-auto">
                                    <button
                                        ref={buttonRef}
                                        type="button"
                                        className="ev-message-card3 ev-btn-xsmall ev-bold-med d-flex align-items-center gap-1 ev-radius-xlarge border"
                                        onClick={handleOpenFilter}
                                    >
                                        <i className={`bi bi-funnel-fill ev-fs-10 ${activeFilterCount > 0 ? "ev-reg-p-blue" : ""}`}></i>
                                        <span className={activeFilterCount > 0 ? "ev-reg-p-blue" : ""}>
                                            Apply Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
                                        </span>
                                    </button>
                                </div>

                                {/* Portal — renders outside any clipping parent */}
                                {isFilterOpen && ReactDOM.createPortal(
                                    <div
                                        ref={panelRef}
                                        style={{ position: "absolute", top: panelPos.top, left: panelPos.left, zIndex: 9999 }}
                                    >
                                        <CTFilterPanel
                                            filters={pendingFilters}
                                            onChange={setPendingFilters}
                                            onApply={(f) => setAppliedFilters({ ...f })}
                                            onClear={() => {
                                                setAppliedFilters({ ...DEFAULT_FILTERS });
                                                setPendingFilters({ ...DEFAULT_FILTERS });
                                            }}
                                            onClose={() => setIsFilterOpen(false)}
                                        />
                                    </div>,
                                    document.body
                                )}
                            </div>
                        </th>
                    </tr>
                </thead>
            </table>

            {/* ── Scrollable rows ── */}
            <div className="scrollable-element vh-50 pe-1" style={{ overflowX: "hidden" }}>
                <table className="table-custom1 overflow-hidden w-100" style={{ tableLayout: "fixed" }}>
                    <colgroup>
                        <col style={{ width: "70%" }} />
                        <col style={{ width: "30%" }} />
                    </colgroup>
                    <tbody>
                        {visibleResults.length === 0 ? (
                            <tr>
                                <td colSpan="2" className="text-center py-5">
                                    <i className="bi bi-funnel ev-fs-50 text-muted mb-3 d-block"></i>
                                    <p className="ev-reg-p-dark ev-bold-med mb-1">
                                        {showSelectedOnly ? "No Selected Trials" : "No Trials Match Filters"}
                                    </p>
                                    <p className="ev-small-p-dark text-muted mb-0">
                                        {showSelectedOnly
                                            ? "Select trials from the list to view them here."
                                            : "Try adjusting or clearing your filters."}
                                    </p>
                                </td>
                            </tr>
                        ) : (
                            visibleResults.map((trial) => (
                                <TrialRow
                                    key={trial.id}
                                    trial={trial}
                                    isActive={selectedResult?.id === trial.id}
                                    isChecked={selectedIds.includes(trial.id)}
                                    onSelect={onSelectResult}
                                    onCheck={onSelectOne}
                                />
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default ClinicalTrialsResultsList;